<template>
  <RightSheet style="width: 90vw;" v-model="dialogVisible" max-width="1920" class="energy-storage-detail-dialog">
    <div class="dialog-wrapper">
      <!-- 标题 -->
      <div class="dialog-title">热管理详情</div>

      <!-- 主内容区域 -->
      <div class="dialog-main">
        <!-- 左侧：消防方阵内容区 -->
        <div class="left-content">
          <!-- 消防方阵折叠面板 - 使用自定义组件 -->
          <ThermalArrayPanel v-for="(array, index) in arrayList" :key="array.id" :ref="el => arrayPanelRefs[index] = el"
            :array-data="array" :highlighted-device="highlightedDevice" :default-expanded="true"
            @open-elink-detail="openElinkDetail" @open-eblock-detail="openEblockDetail"
            @open-eblock-alarm-history="openEblockAlarmHistory" />
        </div>
        <DeviceTree :items="items" :loading="loading" v-model:selectedId="selectedId" @select="onSelect" />

        <!-- 右侧：设备树 -->
        <div class="right-sidebar" v-if="false">
          <!-- 搜索框 -->
          <div class="search-wrapper">
            <v-text-field v-model="searchText" placeholder="按设备名称/UUID搜索设备" rounded variant="outlined" density="compact"
              append-inner-icon="mdi-magnify" clearable hide-details class="search-input"></v-text-field>
          </div>

          <!-- 设备树 -->
          <div class="device-tree">
            <v-treeview :items="treeItems" :search="searchText" :custom-filter="filterDevice" item-value="id"
              item-title="name" activatable open-all density="compact" class="custom-treeview"
              @update:activated="handleTreeItemClick">
              <template v-slot:prepend="{ item }">
                <img v-if="item.type === 'root'" src="@/assets/images/station-monitor/detail-dialog/tree-root-icon.png"
                  style="width: 16px; height: 14px;" alt="根节点" loading="lazy" />
                <img v-else-if="item.type === 'station'"
                  src="@/assets/images/station-monitor/detail-dialog/tree-station-icon.png"
                  style="width: 16px; height: 14px;" alt="电站" loading="lazy" />
                <img v-else-if="item.type === 'category'"
                  src="@/assets/images/station-monitor/detail-dialog/tree-category-icon.png"
                  style="width: 16px; height: 14px;" alt="分类" loading="lazy" />
                <img v-else src="@/assets/images/station-monitor/detail-dialog/tree-device-icon.png"
                  style="width: 16px; height: 14px;" alt="设备" loading="lazy" />
              </template>
            </v-treeview>
          </div>
        </div>
      </div>

      <!-- 关闭按钮 -->
      <v-btn icon size="small" variant="text" class="close-btn" @click="closeDialog">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
  </RightSheet>

  <!-- 详情弹框 -->
  <AirConditionDetailDialog v-model="airConditionDetailVisible" :stationId="currentStationId"
    :elinkUuid="currentDetailElinkUuid" :eblockUuid="currentDetailEblockUuid" :openAlarmHistory="openWithAlarmHistory"
    @close="onCloseDetail" :showBackBtn="false"/>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, shallowRef, nextTick } from 'vue'
import ThermalArrayPanel from './ThermalArrayPanel.vue'
import { useStationContext } from '../../composables/useStationContext'
import {
  EBlockWorkStatus,
  EBlockStatusColor,
  getEBlockOnlineText,
  getEBlockOnlineClass,
  formatStatus
} from './monitorEnums'

import { ws_req } from '@/ws/wss-request'
import useUserStore from '@/store/modules/user'
import RightSheet from '@/components/RightSheet/index.vue'
import AirConditionDetailDialog from './AirConditionDetailDialog.vue'
import { useDeviceList } from '@/hooks/useDeviceList'
import DeviceTree from '../../tabs/EnergyStorageSystem/components/DeviceTree/index.vue'

const { items, loading, getDeviceTypeById } = useDeviceList()
const selectedId = ref([])

const onSelect = (selectedItems) => {
  console.log(selectedItems)
  highlightedDevice.value = {
    type: getDeviceTypeById(selectedItems.id),
    id: selectedItems.id
  }

  const element = document.querySelector(`[data-device-id="${selectedItems.id}"]`)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// 格式化数据
// eLink AC data
const formatElink = (elink) => {
  return {
    id: elink.uuid,
    uuid: elink.uuid,
    name: elink.elink_name,
    model: elink.model || '--',
    coolingMode: '风冷',//getCoolingMethod(elink), // Assuming eLink has this or derived from first eBlock
    // Air Condition list
    airconds: (elink.aircondition?.aircond || []).map(item => formatAirCond(item))
  }
}

// eBlock AC data
const formatBlock = (block, elink) => {
  
  return {
    id: block.uuid,
    uuid: block.uuid,
    name: block.eblock_name,
    model: block.model || '--',
    coolingMode: getCoolingMethod(block),
    // Air Condition list
    airconds: (block.aircond || []).map(item => formatAirCond(item)),
    hasWarning: hasAlarm(block.aircond),
    alarmCount: block.alarmNum || 0 // You might want to calculate real alarm count
  }
}

const formatAirCond = (item) => {
  return {
    idx: item.aircond_idx,
    onOff: item.telemeter?.on_off,
    workState: item.telemeter?.work_state,
    humity: item.telemeter?.humity,
    temp: item.telemeter?.cabinet_in_temp,
    alarm: hasAlarm([item])
  }
}


const MethodMap = {
  eBlock200: '风冷',
  eBlock230: '风冷',
  eBlock172P: '风冷',
  eBlock172S: '风冷',
  eBlock215: '风冷',
}

function getCoolingMethod(record) {
  const model = record.model
  return MethodMap[model] ?? '水冷'
}

const AlarmKeys = new Set([
  'cabin_in_sensor_fault',
  'evap_temp_sensor_fault',
  'conden_temp_sensor_fault',
  'cabin_out_temp_sensor_fault',
  'hum_sensor_fault',
  'hum_high_alarm',
  'fan_in_alarm',
  'fan_out_alarm',
  'conden_high_alarm',
  'evap_frozen_alarm',
  'cooling_alarm',
  'temp_high_alarm',
  'temp_low_alarm',
  'heater_alarm',
  'over_voltage_alarm',
  'under_voltage_alarm',
  'high_low_voltage_alarm',
  'high_low_press_alarm',
  'high_low_temp_alarm',
  'door_alarm',
])

function hasAlarm(aircondList) {
  if (!aircondList || !Array.isArray(aircondList)) return false
  return aircondList.some(aircond => {
    return Object.keys(aircond.telesignal || {}).some(key => {
      let val = aircond.telesignal[key]
      return AlarmKeys.has(key) && val === 1
    })
  })
}

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const searchText = ref('')
let mockTimer = null

const lastStructureKey = ref('')
const arrayPanelRefs = ref([])

const highlightedDevice = ref({ type: null, id: null })
const { stationId: currentStationId, stationName: currentStationName } = useStationContext()
const treeItems = shallowRef([])
const arrayList = shallowRef([])

const airConditionDetailVisible = ref(false)
const currentDetailElinkUuid = ref(null)
const currentDetailEblockUuid = ref(null)
const openWithAlarmHistory = ref(false)

// 详情弹框状态
const openElinkDetail = async (elink) => {
  dialogVisible.value = false
  
  
  currentDetailElinkUuid.value = elink.uuid
  currentDetailEblockUuid.value = null
  openWithAlarmHistory.value = false
  
  // await nextTick()
  // await sleep(300)
  setTimeout(() => {
    airConditionDetailVisible.value = true
  }, 200)
 
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

const openEblockDetail = async (block) => {  
  currentDetailElinkUuid.value = findElinkIdByBlockId(block.uuid)
  currentDetailEblockUuid.value = block.uuid
  openWithAlarmHistory.value = false
  dialogVisible.value = false
  setTimeout(() => {
    airConditionDetailVisible.value = true
  }, 200)
}
const openEblockAlarmHistory = (block) => {
  currentDetailElinkUuid.value = findElinkIdByBlockId(block.uuid)
  currentDetailEblockUuid.value = block.uuid

  dialogVisible.value = false
  openWithAlarmHistory.value = true
  setTimeout(() => {
    airConditionDetailVisible.value = true
  }, 200)
}

// 详情关闭时，打开热管理弹框
async function onCloseDetail() {  
  setTimeout(() => {
    dialogVisible.value = true    
  }, 200);
}

// Helper to find eLink UUID for a given block UUID
const findElinkIdByBlockId = (blockUuid) => {
  for (const array of arrayList.value) {
    if (array.blocks.some(b => b.uuid === blockUuid)) {
      return array.elink.uuid
    }
  }
  return null
}

// 模拟获取数据
const fetchFireFightingOverview = async (recurs = true) => {
  clearTimeout(mockTimer)

  if (!currentStationId.value) return

  try {
    // 检查连接状态
    if (!ws_req.is_open && !ws_req.is_connecting) {
      const userStore = useUserStore()
      await userStore.getInfo()
    }

    const result = await ws_req.request('aircondition', {
      stationId: Number(currentStationId.value)
    })

    if (result && Array.isArray(result)) {
      // 处理数据
      const newArrayList = result.map((item, index) => {
        // 确保 eLink UUID 唯一，如果不存在则使用临时生成
        const elinkUuid = item.uuid || `elink-temp-${index}-${Date.now()}`

        return {
          id: elinkUuid,
          name: `${index + 1}#储能方阵`,
          elink: formatElink({ ...item, uuid: elinkUuid }),
          blocks: (item.eblockList || []).map((block, bIndex) => {
            // 确保 eBlock UUID 唯一
            const blockUuid = block.uuid || `eblock-temp-${index}-${bIndex}-${Date.now()}`            
            return formatBlock({ ...block, uuid: blockUuid }, item)
          }),
          coolingMode: getCoolingMethod(item.eblockList?.[0]),
          eblockModel: item.eblockList?.[0]?.model || '--'
        }
      })

      arrayList.value = newArrayList
      buildTreeItems(newArrayList) // Use processed array list
    }
  } catch (e) {
    console.error('Aircondition WS request failed:', e)
  }

  if (recurs && dialogVisible.value) {
    mockTimer = setTimeout(() => {
      fetchFireFightingOverview(true)
    }, 5000)
  }
}

// 构建树形结构数据
const buildTreeItems = (dataList) => {
  if (!dataList || !Array.isArray(dataList) || dataList.length === 0) {
    treeItems.value = []
    return
  }

  const stationName = currentStationName.value || '当前电站'

  const children = dataList.map((item, index) => {
    // item is the processed object from newArrayList
    const elink = item.elink
    const eBlockList = item.blocks || []

    return {
      id: `category-${elink.uuid}`, // Ensure category ID is unique
      name: `${index + 1}#储能方阵`,
      type: 'category',
      children: [
        {
          id: elink.uuid, // Use UUID as ID
          name: elink.name,
          type: 'elink',
          uuid: elink.uuid
        },
        ...eBlockList.map(block => ({
          id: block.uuid, // Use UUID as ID
          name: block.name,
          type: 'eblock',
          uuid: block.uuid
        }))
      ]
    }
  })

  treeItems.value = [
    {
      id: 'station-root',
      name: stationName,
      type: 'station',
      children: children
    }
  ]
}

watch(dialogVisible, (newVal) => {
  if (newVal) {
    fetchFireFightingOverview(true)
  } else {
    clearTimeout(mockTimer)
  }
})

// Reset openWithAlarmHistory when AirConditionDetailDialog closes
watch(airConditionDetailVisible, (newVal) => {
  if (!newVal) {
    openWithAlarmHistory.value = false
  }
})

const closeDialog = () => {
  dialogVisible.value = false
}

// 处理树节点点击事件 (复用逻辑)
const handleTreeItemClick = (selectedItems) => {
  let itemId = null
  if (Array.isArray(selectedItems)) {
    if (selectedItems.length === 0) return
    itemId = selectedItems[0]
  } else if (selectedItems) {
    itemId = selectedItems
  } else {
    return
  }

  const findItem = (items, targetId) => {
    for (const item of items) {
      if (item.id === targetId) return item
      if (item.children) {
        const found = findItem(item.children, targetId)
        if (found) return found
      }
    }
    return null
  }

  const item = findItem(treeItems.value, itemId)
  if (!item || !item.type) return

  if (item.type === 'elink' || item.type === 'eblock') {
    // 提取设备ID (使用 uuid)
    const deviceId = item.uuid

    const panelIndex = arrayList.value.findIndex((array) => {
      if (item.type === 'elink') {
        return array.elink.uuid === deviceId
      } else {
        return array.blocks.some(block => block.uuid === deviceId)
      }
    })

    if (panelIndex !== -1) {
      arrayPanelRefs.value.forEach(panel => {
        if (panel && panel.expand) {
          panel.expand()
        }
      })

      highlightedDevice.value = { type: item.type, id: deviceId }

      setTimeout(() => {
        const selector = item.type === 'elink'
          ? `.elink-info-card[data-elink-id="${deviceId}"]`
          : `.block-card[data-block-id="${deviceId}"]`
        const element = document.querySelector(selector)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 400)

      setTimeout(() => {
        highlightedDevice.value = { type: null, id: null }
      }, 3000)
    }
  }
}

const filterDevice = (value, search, item) => {
  if (!search) return true
  const searchLower = search.toLowerCase()
  const text = (value || '').toLowerCase()
  const rawItem = item.raw || item
  const uuid = (rawItem.uuid || '').toLowerCase()
  return text.includes(searchLower) || uuid.includes(searchLower)
}

onUnmounted(() => {
  clearTimeout(mockTimer)
})
</script>

<style scoped lang="scss">
.energy-storage-detail-dialog {
  :deep(.v-overlay__content) {
    width: 100vw !important;
    max-width: 1920px !important;
    height: 950px !important;
    margin: 0 auto !important;
    border-radius: 30px 30px 0 0 !important;
  }
}

.dialog-wrapper {
  position: relative;
  width: 100%;
  height: 100vh;
  padding: 20px 24px 0;
  background-color: #fff !important;
  background: url('@/assets/images/station-monitor/detail-dialog/detail-bg.svg') no-repeat center center;
  background-size: cover;
  border-radius: 30px 30px 0 0 !important;
}

.dialog-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
  margin-bottom: 20px;
  padding-left: 24px;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 10;
}

.dialog-main {
  display: flex;
  flex-direction: row-reverse;
  gap: 16px;
  height: calc(100% - 83px);
  background: rgba(246, 251, 255, 1);
  border-radius: 30px;
  padding: 20px;
}

.left-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
  will-change: scroll-position;
  transform: translateZ(0);
  -webkit-overflow-scrolling: touch;
  min-height: 0;
}

.right-sidebar {
  width: 319px;
  background: rgba(255, 255, 255, 1);
  border-radius: 20px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.search-wrapper {
  position: relative;
  margin-bottom: 26px;
}

.search-input {
  width: 100%;
}

.device-tree {
  flex: 1;
  overflow-y: auto;
}

.custom-treeview {
  :deep(.v-treeview-item) {
    margin-bottom: 8px;
  }

  :deep(.v-treeview-item__label) {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 14px;
    color: rgba(0, 0, 0, 1);
    line-height: normal;
  }

  :deep(.v-treeview-item--active) {
    background: rgba(239, 242, 254, 1);
    border-radius: 3px;

    .v-treeview-item__label {
      color: rgba(58, 104, 255, 1);
    }
  }

  :deep(.v-treeview-item__prepend) {
    margin-right: 9px;
  }
}
</style>
